import type { GridApi, IRowNode } from "ag-grid-community";

import { CellContentType } from "@/routes/smart-table/utils/contentType";
import { parseAnchorCellValue } from "@/routes/smart-table/utils/parseAnchorCellValue";

export const TRACE_DISPLAY_MODE = "trace" as const;
export const REQUEST_LOG_DISPLAY_MODE = "request_log" as const;
export const MAX_AUTO_FORMAT_JSON_LENGTH = 50_000;

export type TraceDisplayMode = typeof TRACE_DISPLAY_MODE;
export type RequestLogDisplayMode = typeof REQUEST_LOG_DISPLAY_MODE;

export const shouldRenderLargeJsonAsRaw = (
  isResidentContentTruncated: boolean,
  hasFetchedFullCell: boolean,
): boolean => isResidentContentTruncated && !hasFetchedFullCell;

export const getAnchorCellViewId = (rawValue: unknown, metricKey: string | null): string | null =>
  metricKey === null ? parseAnchorCellValue(rawValue).traceId : null;

/** The request id stored on a Request anchor cell value, if any. */
export const getRequestCellViewId = (rawValue: unknown, metricKey: string | null): string | null =>
  metricKey === null ? parseAnchorCellValue(rawValue).requestId : null;

export interface PromptCellSnippet {
  name: string;
  versionNumber: number;
}

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

/** Snippet versions recorded in a prompt cell's frozen_template_data when it was computed. */
export const getPromptCellSnippets = (rawValue: unknown): PromptCellSnippet[] => {
  const root = asRecord(rawValue);
  const frozenTemplateData = asRecord(
    root?.frozen_template_data ?? asRecord(root?.value)?.frozen_template_data,
  );
  const snippets = frozenTemplateData?.snippets;
  if (!Array.isArray(snippets)) {
    return [];
  }

  return snippets.flatMap(snippet => {
    const record = asRecord(snippet);
    const name = record?.prompt_template_name;
    const versionNumber = record?.version_number;
    return typeof name === "string" && typeof versionNumber === "number"
      ? [{ name, versionNumber }]
      : [];
  });
};

export const formatPromptCellSnippets = (snippets: PromptCellSnippet[]): string =>
  snippets.map(({ name, versionNumber }) => `${name} v${versionNumber}`).join(", ");

export const getDataRowIndex = (data: unknown): number | null => {
  if (!data || typeof data !== "object") {
    return null;
  }

  const rowIndex = (data as { _rowIndex?: unknown })._rowIndex;
  return typeof rowIndex === "number" && Number.isFinite(rowIndex) ? rowIndex : null;
};

export const getDisplayedRowNodeByDataRowIndex = (
  api: GridApi,
  rowIndex: number,
): IRowNode | undefined => {
  const displayedRowCount = api.getDisplayedRowCount();
  for (let displayedRowIndex = 0; displayedRowIndex < displayedRowCount; displayedRowIndex += 1) {
    const rowNode = api.getDisplayedRowAtIndex(displayedRowIndex);
    if (getDataRowIndex(rowNode?.data) === rowIndex) {
      return rowNode ?? undefined;
    }
  }
  return undefined;
};

export function computeInitialEditText(
  displayValue: string | null,
  contentType: CellContentType,
): string {
  const text = displayValue ?? "";
  if (contentType === CellContentType.JSON && text.length <= MAX_AUTO_FORMAT_JSON_LENGTH) {
    try {
      return JSON.stringify(JSON.parse(text), null, 2);
    } catch {
      return text;
    }
  }

  return text;
}

export function stringifyExactDisplayValue(value: unknown): string | null {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === "string") {
    return value;
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

/** Prefer the span/trace `name` for grid labels after editing the full JSON payload. */
export function extractTraceCellDisplayName(value: unknown): string | null {
  return parseAnchorCellValue(value).name;
}
